import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import type { PilloryPost, PaginatedResponse } from '@/types';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Target, Clock, ThumbsUp, Users, Share2, Check } from 'lucide-react';
import { trackEvent } from '@/lib/faro';
import { ProfileLink } from '@/components/ProfileLink';

function timeRemaining(expiresAt: string): string {
    const diff = new Date(expiresAt).getTime() - Date.now();
    if (diff <= 0) return 'Expired';
    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    if (hours > 0) return `${hours}h ${minutes}m left`;
    return `${minutes}m left`;
}

function PilloryCard({ post }: { post: PilloryPost }) {
    const { user } = useAuth();
    const queryClient = useQueryClient();
    const [copied, setCopied] = useState(false);

    const shareUrl = `${window.location.origin}/pillory/share/${post.share_token}`;
    const handleShare = () => {
        const copy = navigator.clipboard
            ? navigator.clipboard.writeText(shareUrl)
            : new Promise<void>((resolve) => {
                  const ta = document.createElement('textarea');
                  ta.value = shareUrl;
                  ta.style.position = 'fixed';
                  ta.style.opacity = '0';
                  document.body.appendChild(ta);
                  ta.select();
                  document.execCommand('copy');
                  document.body.removeChild(ta);
                  resolve();
              });
        copy.then(() => {
            setCopied(true);
            trackEvent('pillory_shared', { post_id: post.id });
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const voteMutation = useMutation({
        mutationFn: () => api.post<{ vote_count: number; total_added: string }>(`/pillory/${post.id}/vote`, {}),
        onSuccess: () => {
            trackEvent('pillory_voted');
            queryClient.invalidateQueries({ queryKey: ['pillory'] });
        },
    });

    const canVote = post.is_active && user && (post.user_votes ?? 0) < post.max_votes_per_user;
    const isParticipant = user && (user.id === post.dom?.id || user.id === post.sub?.id);

    return (
        <Card className="border-orange-900/30">
            <CardContent className="py-4">
                <div className="flex items-start gap-3">
                    {post.sub?.avatar_path ? (
                        <img
                            src={post.sub.avatar_path}
                            alt={post.sub.display_name}
                            className="h-10 w-10 shrink-0 rounded-full object-cover"
                        />
                    ) : (
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-orange-900/30 text-orange-400 font-bold">
                            {post.sub?.display_name?.[0]?.toUpperCase() ?? '?'}
                        </div>
                    )}

                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                            {post.sub ? (
                                <ProfileLink
                                    userId={post.sub.id}
                                    displayName={post.sub.display_name}
                                    className="font-semibold text-orange-400 hover:underline"
                                />
                            ) : (
                                <span className="font-semibold text-orange-400">Unknown</span>
                            )}
                            <span className="text-sm text-slate-500">posted to the pillory by</span>
                            {post.dom ? (
                                <ProfileLink
                                    userId={post.dom.id}
                                    displayName={post.dom.display_name}
                                    className="text-violet-400 hover:underline text-sm"
                                />
                            ) : (
                                <span className="text-sm text-slate-400">Unknown</span>
                            )}
                        </div>

                        <p className="mt-1 text-sm text-slate-300 italic">"{post.reason}"</p>

                        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                            <Badge variant="outline" className="gap-1 border-orange-800 text-orange-400">
                                <ThumbsUp className="h-3 w-3" />
                                {post.vote_count} vote{post.vote_count !== 1 ? 's' : ''}
                            </Badge>
                            <span className="text-slate-400">
                                ${parseFloat(post.fee_per_vote).toFixed(2)}/vote
                            </span>
                            <span className="text-red-400 font-medium">
                                ${parseFloat(post.total_added).toFixed(2)} added
                            </span>
                            <span className="flex items-center gap-1 text-slate-500">
                                <Clock className="h-3 w-3" />
                                {timeRemaining(post.expires_at)}
                            </span>
                            {post.max_votes_per_user > 1 && (
                                <span className="flex items-center gap-1 text-slate-500">
                                    <Users className="h-3 w-3" />
                                    max {post.max_votes_per_user}/user
                                </span>
                            )}
                        </div>

                        <div className="mt-3 flex flex-wrap items-center gap-2">
                            {post.is_active && !isParticipant && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    className="border-orange-800 text-orange-400 hover:bg-orange-900/30"
                                    disabled={!canVote || voteMutation.isPending}
                                    onClick={() => voteMutation.mutate()}
                                >
                                    {voteMutation.isPending
                                        ? 'Voting...'
                                        : !canVote
                                          ? 'Max votes reached'
                                          : `Vote (+$${parseFloat(post.fee_per_vote).toFixed(2)})`}
                                </Button>
                            )}
                            {!post.is_active && (
                                <Badge variant="secondary" className="text-[10px]">
                                    Expired
                                </Badge>
                            )}
                            <Button
                                size="sm"
                                variant="ghost"
                                className="text-slate-400 hover:text-slate-200"
                                onClick={(e) => { e.stopPropagation(); handleShare(); }}
                            >
                                {copied ? <Check className="h-3.5 w-3.5 mr-1" /> : <Share2 className="h-3.5 w-3.5 mr-1" />}
                                {copied ? 'Copied!' : 'Share'}
                            </Button>
                        </div>
                        {voteMutation.isError && (
                            <p className="mt-1 text-xs text-red-400">
                                {voteMutation.error instanceof ApiError
                                    ? voteMutation.error.message
                                    : 'Failed to vote'}
                            </p>
                        )}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

export default function PilloryPage() {
    const { data, isLoading } = useQuery({
        queryKey: ['pillory'],
        queryFn: () => api.get<PaginatedResponse<PilloryPost>>('/pillory'),
    });

    const posts = data?.data ?? [];

    return (
        <div className="mx-auto max-w-xl space-y-6">
            <div className="text-center">
                <h1 className="flex items-center justify-center gap-2 text-2xl font-bold text-orange-400">
                    <Target className="h-6 w-6" />
                    The Pillory
                    <Target className="h-6 w-6" />
                </h1>
                <p className="mt-1 text-sm text-slate-500">
                    Vote to punish subs posted here — each vote adds to their debt
                </p>
            </div>

            {isLoading && (
                <p className="text-center text-sm text-slate-500">Loading...</p>
            )}

            {!isLoading && posts.length === 0 && (
                <Card>
                    <CardContent className="py-12 text-center text-sm text-slate-500">
                        No active pillory posts. All subs are safe... for now.
                    </CardContent>
                </Card>
            )}

            <div className="space-y-3">
                {posts.map((post) => (
                    <PilloryCard key={post.id} post={post} />
                ))}
            </div>
        </div>
    );
}
